import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Notification Context
 * 
 * A React context that provides a centralized notification system for the application.
 * Features include:
 * - Toast notifications that appear temporarily in the corner of the screen
 * - Persistent notification history accessible via the notification bell
 * - Different notification types (success, error, warning, info)
 * - Read/unread status tracking
 */
const NotificationContext = createContext();

// Maximum number of notifications to store in history
const MAX_NOTIFICATIONS = 50;

/**
 * Types of notifications supported by the system
 * Used to determine styling and default titles
 */
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * NotificationProvider Component
 * 
 * Provides notification functionality to the application.
 * Wrap your app or a section of your app with this provider to use the notification system.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} NotificationProvider component
 */
export function NotificationProvider({ children }) {
  // State for notification history (permanent storage)
  const [notifications, setNotifications] = useState([]);
  // State for active toast notifications (temporary, visible on screen)
  const [toasts, setToasts] = useState([]);

  /**
   * Add a new notification to both history and active toasts
   * 
   * @param {string} message - The notification message
   * @param {string} [type=NOTIFICATION_TYPES.INFO] - Notification type (success, error, warning, info)
   * @param {string} [title=''] - Optional title (defaults based on type if not provided)
   * @param {number} [duration=8000] - How long the toast should display in ms (0 for no auto-dismiss)
   * @returns {number} Notification ID that can be used to reference this notification
   */
  const addNotification = (message, type = NOTIFICATION_TYPES.INFO, title = '', duration = 8000) => {
    const newNotification = {
      id: Date.now(),
      message,
      type,
      title: title || getDefaultTitle(type),
      timestamp: new Date(),
      read: false
    };

    // Add to notifications history
    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      // Keep only the latest MAX_NOTIFICATIONS
      return updated.slice(0, MAX_NOTIFICATIONS);
    });

    // Add to active toasts
    setToasts(prev => [newNotification, ...prev]);

    // Auto-remove toast after duration, unless duration is 0 (meaning keep until manually closed)
    if (duration > 0) {
      setTimeout(() => {
        removeToast(newNotification.id);
      }, duration);
    }

    return newNotification.id;
  };

  /**
   * Get default title based on notification type
   * 
   * @param {string} type - Notification type
   * @returns {string} Default title for the given type
   * @private
   */
  const getDefaultTitle = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return 'Success';
      case NOTIFICATION_TYPES.ERROR:
        return 'Error';
      case NOTIFICATION_TYPES.WARNING:
        return 'Warning';
      case NOTIFICATION_TYPES.INFO:
        return 'Information';
      default:
        return '';
    }
  };

  /**
   * Remove a toast notification by ID
   * Only affects the toast display, not the notification history
   * 
   * @param {number} id - ID of the notification to remove
   */
  const removeToast = (id) => {
    console.log('Removing toast with id:', id);
    // Ensure the toast is removed from the toasts array
    setToasts(prev => {
      const filtered = prev.filter(toast => toast.id !== id);
      console.log('Filtered toasts, removed count:', prev.length - filtered.length);
      return filtered;
    });
  };

  /**
   * Mark a notification as read in the history
   * 
   * @param {number} id - ID of the notification to mark as read
   */
  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  /**
   * Clear all notifications from both history and active toasts
   */
  const clearAll = () => {
    setNotifications([]);
    setToasts([]);
  };

  // Shorthand methods for specific notification types
  const success = (message, title, duration) => 
    addNotification(message, NOTIFICATION_TYPES.SUCCESS, title, duration);
  
  const error = (message, title, duration) => 
    addNotification(message, NOTIFICATION_TYPES.ERROR, title, duration);
  
  const warning = (message, title, duration) => 
    addNotification(message, NOTIFICATION_TYPES.WARNING, title, duration);
  
  const info = (message, title, duration) => 
    addNotification(message, NOTIFICATION_TYPES.INFO, title, duration);

  // Get unread count
  const getUnreadCount = () => {
    return notifications.filter(notification => !notification.read).length;
  };

  // Value to provide through the context
  const value = {
    notifications,
    toasts,
    addNotification,
    removeToast,
    markAsRead,
    markAllAsRead,
    clearAll,
    success,
    error,
    warning,
    info,
    getUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Custom hook to use the notification context
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext; 